import { getAuth, onAuthStateChanged } from "firebase/auth";
import { Suspense, useEffect, useRef } from "react";
import { Toaster } from "react-hot-toast";
import { useIntercom } from "react-use-intercom";
import { AuthProvider, useFirebaseApp } from "reactfire";
import Loader from "./components/ui/Loader";
import { GrowthBookProvider, growthbook, initGrowthbook } from "./growthbook";
import { getIntercomBootProps } from "./services/intercom";

import { RouterProvider } from "@/lib/router";
import { appRouter, queryClient } from "@/lib/router/instances";
import { QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { resetSaleMode } from "./components/SaleMode/resetSaleMode";
import {
  identifyCustomerIOUser,
  resetCustomerIOUser,
} from "./services/customerio";
import "./services/i18n/config";
import { identifyUser } from "./services/posthog";
import { getSentryCurrentScope, setSentryUser } from "./services/sentry";
import { captureInitialUtm } from "./tracking/utm-attribution";

const mode = import.meta.env.MODE;

const App = () => {
  const app = useFirebaseApp();
  const auth = getAuth(app);
  const { boot, shutdown } = useIntercom();
  const lastIdentifiedUid = useRef<string | null>(null);

  useEffect(() => {
    captureInitialUtm();

    const params = new URLSearchParams(window.location.search);
    let referrer_code = params.get("referrer");
    if (referrer_code) {
      localStorage.setItem("referrer_code", referrer_code);
    }

    let planType = params.get("planType");
    if (planType) {
      localStorage.setItem("planType", planType);
    }

    onAuthStateChanged(auth, (user) => {
      if (user !== null) {
        shutdown();
        boot(getIntercomBootProps(user));
        getSentryCurrentScope().setUser({
          email: user.email!,
          userId: user.uid,
        });
        identifyUser(user.uid, user.email!);
        growthbook.setAttributes({
          id: user.uid,
          user_id: user.uid,
          email: user.email,
          name: user.displayName,
        });
        // email is the Customer.io userId and is nullable
        if (user.email && lastIdentifiedUid.current !== user.uid) {
          lastIdentifiedUid.current = user.uid;
          identifyCustomerIOUser(
            { email: user.email, uid: user.uid },
            { name: user.displayName ?? undefined },
          );
        }
        resetSaleMode();
      } else {
        shutdown();
        boot();
        setSentryUser(null);
        // Only on a real sign-out, so anonymous ids survive across visits
        if (lastIdentifiedUid.current) {
          lastIdentifiedUid.current = null;
          resetCustomerIOUser();
        }
      }
    });
  }, []);

  useEffect(() => {
    initGrowthbook();
  }, []);

  console.log("[APP] mode", mode);

  return (
    <Suspense fallback={<Loader />}>
      <QueryClientProvider client={queryClient}>
        <GrowthBookProvider growthbook={growthbook}>
          <AuthProvider sdk={auth}>
            <HelmetProvider>
              <Toaster
                containerStyle={{
                  top: 40,
                }}
              />
              <RouterProvider router={appRouter} />
            </HelmetProvider>
          </AuthProvider>
        </GrowthBookProvider>
      </QueryClientProvider>
    </Suspense>
  );
};

export default App;
